import { extend } from '@react-three/fiber'
import { useGLTF, useTexture } from '@react-three/drei'
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'
import { useState } from 'react'
import { Vector3, PlaneGeometry, NearestFilter, Color } from 'three'
import { useControls } from 'leva'
import { Flower } from './Flower'
import useScrollStore from '../useScrollStore'
import { useEffect, useRef } from 'react'

extend({ PlaneGeometry })

type ExtendedGLTF = GLTF & {
  nodes: any
  materials: any
  animations: any
}

export const Tree = ({ position }: { position: Vector3 }) => {
  const steps = [
    //0.2806, 0.3417, 0.4, 0.8056, 0.8194
    {totalScrollProgress: 0.0, stage: 0}, 
    {totalScrollProgress: 0.05, stage: 1},
    {totalScrollProgress: 0.2, stage: 2},
    {totalScrollProgress: 0.25, stage: 3},
      {totalScrollProgress: 0.3, stage: 4},
      {totalScrollProgress: 0.4, stage: 5},
      {totalScrollProgress: 0.45, stage: 6},
      {totalScrollProgress: 0.475, stage: 0},
      {totalScrollProgress: 0.5, stage: 1},
      {totalScrollProgress: 0.6, stage: 2},
      {totalScrollProgress: 0.65, stage: 3},
      {totalScrollProgress: 0.7, stage: 4},
      {totalScrollProgress: 0.8, stage: 5},
      {totalScrollProgress: 0.85, stage: 6},
      {totalScrollProgress: 0.9, stage: 1},
  ]
  const [treeMeshRef, setTreeMeshRef] = useState<any>(null)
  const [{ shadowColor, trunkColor, stage }, setControls] = useControls(
    'Seasons',
    () => ({
      shadowColor: { r: 20, g: 13, b: 10 },
      trunkColor: { r: 62, g: 48, b: 40 },
      stage: { value: 0, min: 0, max: 6, step: 1 },
    })
  )
  const stageRef = useRef(0)
  const lastStageRef = useRef(0)
  const models = useGLTF(require('./tree.glb')) as ExtendedGLTF
  const [treeColor, treeRoughness, treeMetallic] = useTexture([
    require('./tree-color.png'),
    require('./tree-roughness.png'),
    require('./tree-metallic.png'),
  ])

  treeColor.flipY = false
  // Prevent the seams from appearing
  treeColor.magFilter = NearestFilter
  treeColor.minFilter = NearestFilter
  treeRoughness.flipY = false
  treeRoughness.minFilter = NearestFilter

  console.log('Render tree')

  const times = {
    leaves: {
      blooming: [1, 2, 3, 4, 5].includes(stage) ? 1 : 0,
      coloring: [4, 5, 6].includes(stage) ? 1 : 0,
      falling: [5, 6].includes(stage) ? 1 : 0,
    },
    flowers: {
      blooming: [2, 3].includes(stage) ? 1 : 0,
      coloring: 0,
      falling: [3, 4].includes(stage) ? 1 : 0,
    },
  }

  useEffect(
    () =>
      useScrollStore.subscribe((state) => {
        for (let i = steps.length - 1; i >= 0; i--) {
          stageRef.current = 0
          if (state.scrollPct > steps[i].totalScrollProgress) {
            stageRef.current = steps[i].stage
            break
          }
        }

        if (lastStageRef.current !== stageRef.current) {
          lastStageRef.current = stageRef.current
          setControls({ stage: stageRef.current })
        }
      }),
    []
  )

  return (
    <group position={position}>
      <mesh
        ref={(newRef) => setTreeMeshRef(newRef)}
        geometry={models.nodes.tree.geometry}
        castShadow
      >
        <meshPhysicalMaterial
          map={treeColor}
          roughnessMap={treeRoughness}
          metalnessMap={treeMetallic}
          roughness={1.35}
        />
      </mesh>

      {treeMeshRef && (
        <>
          <Flower
            tree={treeMeshRef}
            petal={models.nodes.petal}
            maxFlowersCount={1000}
            name='flowers'
            color={new Color(1, 1, 1)}
            emissive={new Color(1, 1, 1)}
            _posAlongStem={2}
            _size={0.1}
            _bottomRadius={0}
            _topRadius={4}
            _flowersCount={750}
            // _posAlongStem={2.25}
            // _size={0.1}
            // _bottomRadius={0.045}
            // _flowersCount={750}
            _bloomingTime={times.flowers.blooming}
            _coloringTime={times.flowers.coloring}
            _fallingTime={times.flowers.falling}
            _animate={stage !== 0}
            type='chestnut'
          />

          <Flower
            tree={treeMeshRef}
            petal={models.nodes.leaf}
            maxFlowersCount={1000}
            color={new Color(0.4, 0.6, 0.5)}
            _bottomRadius={0}
            _petalsCount={7}
            _posAlongStem={2}
            // emissive={new Color(0.4 * 0.1, 0.6 * 0.1, 0.5 * 0.1)}
            name='leaves'
            _flowersCount={1000}
            _bloomingTime={times.leaves.blooming}
            _coloringTime={times.leaves.coloring}
            _fallingTime={times.leaves.falling}
            _animate={stage !== 0}
          />
        </>
      )}
    </group>
  )
}
